import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";
import { applyTheme } from "./theme";

const ContentContext = createContext({
  content: null,
  loading: true,
  error: null,
  refresh: () => {},
});
const LEGACY_SITE_NAME = /ArchDigital(?: Studio)?/g;

export function getSiteTitle(content) {
  return (content?.site_title || content?.nav_logo_main || "Website").trim();
}

export function getSiteName(content) {
  const title = getSiteTitle(content);
  return title.split(/\s+(?:[-–—|:])\s+/)[0]?.trim() || title;
}

export function getSiteDescription(content) {
  return (
    content?.site_description ||
    content?.hero_subtext ||
    "A curated portfolio website."
  ).trim();
}

export function replaceLegacySiteName(value, content) {
  if (typeof value !== "string") return value;
  return value.replace(LEGACY_SITE_NAME, getSiteName(content));
}

function applySiteIcon(content) {
  if (typeof document === "undefined") return;
  const href = content?.nav_site_icon_url || content?.nav_logo_image_url || "";
  if (!href) return;

  let link = document.querySelector('link[rel="icon"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = href;
}

function applySiteMetadata(content) {
  if (typeof document === "undefined") return;

  const title = (content?.site_title || "").trim();
  const description = (content?.site_description || "").trim();

  if (title) document.title = title;

  let meta = document.querySelector('meta[name="description"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "description";
    document.head.appendChild(meta);
  }
  if (description) meta.content = description;
}

export function ContentProvider({ children }) {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/content");
      setContent(res.data);
      applyTheme(res.data);
      applySiteIcon(res.data);
      applySiteMetadata(res.data);
    } catch (e) {
      console.error("Failed to load site content", e);
      setError(e);
      setContent(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (loading && !content) {
    return (
      <ContentContext.Provider value={{ content, loading, error, refresh }}>
        <div
          className="min-h-screen flex items-center justify-center bg-ink-900 text-bone-200/50 font-mono text-xs uppercase tracking-[0.3em]"
          data-testid="site-content-loading"
        >
          Loading site…
        </div>
      </ContentContext.Provider>
    );
  }

  if (error && !content) {
    return (
      <ContentContext.Provider value={{ content, loading, error, refresh }}>
        <div
          className="min-h-screen flex items-center justify-center bg-ink-900 text-bone-200/60 px-6 text-center"
          data-testid="site-content-error"
        >
          <div>
            <div className="font-display text-3xl tracking-tightest mb-3">
              Site content unavailable.
            </div>
            <button
              type="button"
              onClick={refresh}
              className="site-button font-mono text-[11px] uppercase tracking-[0.25em] border border-bone-50/20 px-4 py-3 hover:border-ember hover:text-ember"
            >
              Try again
            </button>
          </div>
        </div>
      </ContentContext.Provider>
    );
  }

  return (
    <ContentContext.Provider value={{ content, loading, error, refresh }}>
      {children}
    </ContentContext.Provider>
  );
}

export function useContent() {
  return useContext(ContentContext);
}
